import React, { useState, useMemo } from 'react';
import { f0, fc } from '../utils';

const GOAL_COLORS = ['#6bcb77', '#4d96ff', '#ffd93d', '#ff6bdf', '#7C6FF7', '#ff6b6b', '#f0a500'];
const GOAL_EMOJIS = ['🎯', '✈️', '🚗', '🏠', '🛡️', '📚', '💎', '⚡', '🎮', '🏖️'];

export default function Savings({ state, updateState, showToast, triggerConfetti }) {
  const { extraGoals, savDeposits, savContrib } = state;
  const goals = extraGoals || [];

  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editBal, setEditBal] = useState('');
  const [form, setForm] = useState({
    name: '',
    type: 'goal',
    target: '',
    col: GOAL_COLORS[0],
    emoji: '🎯',
  });

  const totalBal = goals.reduce((s, g) => s + (g.bal || 0), 0);
  const totalTarget = goals
    .filter((g) => g.type !== 'account' && g.target > 0)
    .reduce((s, g) => s + g.target, 0);

  const depositsByGoal = useMemo(() => {
    const res = {};
    Object.entries(savDeposits || {}).forEach(([mo, deps]) => {
      (deps || []).forEach((dep) => {
        const gid = dep.goalId || 'unallocated';
        if (!res[gid]) res[gid] = {};
        res[gid][mo] = (res[gid][mo] || 0) + dep.amt;
      });
    });
    return res;
  }, [savDeposits]);

  const addGoal = () => {
    if (!form.name) {
      showToast('Enter a name');
      return;
    }
    if (form.type !== 'account' && !form.target) {
      showToast('Enter a target amount');
      return;
    }
    const goal = {
      id: 'goal_' + Date.now(),
      name: form.name,
      type: form.type,
      target: form.type !== 'account' ? parseFloat(form.target) || 0 : 0,
      bal: 0,
      col: form.col,
      emoji: form.emoji,
    };
    updateState({ extraGoals: [...goals, goal] });
    setForm({ name: '', type: 'goal', target: '', col: GOAL_COLORS[0], emoji: '🎯' });
    setAdding(false);
    showToast('Added!');
  };

  const removeGoal = (id) => {
    if (!window.confirm('Remove this goal?')) return;
    // Strip goalId from any deposit entries linked to this goal so they don't
    // stay orphaned and silently inflate monthly savings totals
    const cleanedDeposits = {};
    Object.entries(savDeposits || {}).forEach(([mo, deps]) => {
      cleanedDeposits[mo] = (deps || []).map((d) =>
        d.goalId === id ? { amt: d.amt, ts: d.ts } : d
      );
    });
    updateState({ extraGoals: goals.filter((g) => g.id !== id), savDeposits: cleanedDeposits });
  };

  const saveBal = (id) => {
    const newBal = parseFloat(editBal) || 0;
    const goal = goals.find((g) => g.id === id);
    updateState({
      extraGoals: goals.map((g) => (g.id === id ? { ...g, bal: newBal } : g)),
    });
    setEditId(null);
    if (goal && goal.target > 0 && newBal >= goal.target && (goal.bal || 0) < goal.target) {
      triggerConfetti?.();
      showToast(`🎉 ${goal.name} goal reached!`);
    } else {
      showToast('Balance updated!');
    }
  };

  const goalGroups = [
    { label: '🎯 Goals', items: goals.filter((g) => g.type !== 'account'), key: 'goal' },
    {
      label: '🏦 Savings Accounts',
      items: goals.filter((g) => g.type === 'account'),
      key: 'account',
    },
  ];

  return (
    <div>
      {/* Header totals */}
      <div className="card cp" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--mut)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
              }}
            >
              💵 Total Saved
            </div>
            <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--grn)', marginTop: 2 }}>
              {f0(totalBal)}
            </div>
          </div>
          {totalTarget > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--mut)' }}>across all goals</div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>
                {Math.round(Math.min(100, (totalBal / totalTarget) * 100))}% of {f0(totalTarget)}
              </div>
            </div>
          )}
        </div>
        {savContrib > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--mut)' }}>
            📅 <strong style={{ color: 'var(--txt)' }}>{fc(savContrib)}/mo</strong> planned ·{' '}
            {fc(savContrib * 12)}/yr
          </div>
        )}
        {goals.length === 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--mut)' }}>
            Add goals and accounts below to start tracking your savings.
          </div>
        )}
      </div>

      {goalGroups.map((group) => {
        if (!group.items.length) return null;
        return (
          <div key={group.key}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: 'var(--mut)',
                marginBottom: 8,
                paddingLeft: 2,
              }}
            >
              {group.label}
            </div>
            <div className="dcg" style={{ marginBottom: 16 }}>
              {group.items.map((g) => {
                const isAccount = g.type === 'account';
                const pct =
                  !isAccount && g.target > 0
                    ? Math.min(100, Math.round((g.bal / g.target) * 100))
                    : 0;
                const rem = !isAccount && g.target > 0 ? Math.max(0, g.target - g.bal) : 0;
                const done = !isAccount && g.target > 0 && g.bal >= g.target;
                const gDeps = depositsByGoal[g.id] || {};
                const recentMos = Object.keys(gDeps).sort().reverse().slice(0, 3);
                // Use this goal's actual recent deposit rate if available; otherwise split
                // savContrib proportionally across all goals that still need funding.
                const goalsNeedingFunds = goals.filter((x) => x.type !== 'account' && x.target > 0 && x.bal < x.target).length || 1;
                const avgMonthly = recentMos.length > 0
                  ? recentMos.reduce((s, mo) => s + (gDeps[mo] || 0), 0) / recentMos.length
                  : 0;
                const etaRate = avgMonthly > 0 ? avgMonthly : savContrib > 0 ? savContrib / goalsNeedingFunds : 0;
                const eta = etaRate > 0 && rem > 0 ? Math.ceil(rem / etaRate) : null;
                const isEditing = editId === g.id;

                return (
                  <div key={g.id} className="dc" style={{ borderColor: g.col || 'var(--bdr)' }}>
                    <div
                      style={{
                        height: 3,
                        background: g.col || 'var(--ylw)',
                        margin: '-16px -18px 12px',
                        borderRadius: '14px 14px 0 0',
                      }}
                    />
                    {done && (
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: 'var(--grn)',
                          background: 'rgba(107,203,119,.12)',
                          border: '1px solid rgba(107,203,119,.3)',
                          borderRadius: 6,
                          padding: '2px 8px',
                          marginBottom: 8,
                          display: 'inline-block',
                        }}
                      >
                        ✓ Goal Reached!
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>{g.emoji || (isAccount ? '🏦' : '🎯')}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{g.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                          {isAccount ? 'Account' : `Goal · ${f0(g.target)}`}
                        </div>
                      </div>
                      <button
                        className="ebt"
                        onClick={() => {
                          setEditId(isEditing ? null : g.id);
                          setEditBal(String(g.bal || 0));
                        }}
                      >
                        ✏️
                      </button>
                      <button className="del" onClick={() => removeGoal(g.id)}>
                        ✕
                      </button>
                    </div>

                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 800,
                        color: g.col || 'var(--grn)',
                        marginBottom: !isAccount && g.target > 0 ? 6 : 10,
                      }}
                    >
                      {f0(g.bal)}
                    </div>

                    {!isAccount && g.target > 0 && (
                      <>
                        <div className="pw" style={{ marginBottom: 4 }}>
                          <div
                            className="pb"
                            style={{ background: g.col || 'var(--grn)', width: pct + '%' }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 10,
                            color: 'var(--mut)',
                            marginBottom: 8,
                          }}
                        >
                          <span>
                            {pct}% · {f0(rem)} to go
                          </span>
                          {eta && (() => {
                            const d = new Date();
                            d.setMonth(d.getMonth() + eta);
                            return <span style={{ color: 'var(--ylw)', fontWeight: 600 }}>~{d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>;
                          })()}
                        </div>
                      </>
                    )}

                    {isEditing && (
                      <div
                        style={{ borderTop: '1px solid var(--bdr)', paddingTop: 10, marginTop: 4 }}
                      >
                        <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 6 }}>
                          Rename
                        </div>
                        <input
                          className="ob-inp"
                          defaultValue={g.name}
                          placeholder="Goal name"
                          style={{ marginBottom: 8 }}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v && v !== g.name)
                              updateState({
                                extraGoals: goals.map((x) =>
                                  x.id === g.id ? { ...x, name: v } : x
                                ),
                              });
                          }}
                        />
                        <div style={{ fontSize: 11, color: 'var(--mut)', marginBottom: 6 }}>
                          Update balance
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div className="ob-amt-wrap" style={{ flex: 1 }}>
                            <span>$</span>
                            <input
                              type="number"
                              min="0"
                              value={editBal}
                              onChange={(e) => setEditBal(e.target.value)}
                            />
                          </div>
                          <button className="bsave" onClick={() => saveBal(g.id)}>
                            Save
                          </button>
                          <button className="bcancel" onClick={() => setEditId(null)}>
                            ✕
                          </button>
                        </div>
                      </div>
                    )}

                    {recentMos.length > 0 && !isEditing && (
                      <div
                        style={{ borderTop: '1px solid var(--bdr)', paddingTop: 8, marginTop: 4 }}
                      >
                        <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 4 }}>
                          Recent deposits
                        </div>
                        {recentMos.map((mo) => (
                          <div
                            key={mo}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              fontSize: 11,
                              padding: '2px 0',
                            }}
                          >
                            <span style={{ color: 'var(--mut)' }}>{mo}</span>
                            <span style={{ fontWeight: 700, color: 'var(--grn)' }}>
                              {fc(gDeps[mo])}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Add card */}
      <div className="dcg" style={{ marginBottom: 12 }}>
        {adding ? (
          <div className="dc" style={{ borderStyle: 'dashed', borderColor: 'var(--ylw)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ylw)', marginBottom: 12 }}>
              + New Savings Bucket
            </div>

            <div className="ef">
              <label>Type</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  ['goal', '🎯 Goal'],
                  ['account', '🏦 Account'],
                ].map(([id, lbl]) => (
                  <button
                    key={id}
                    onClick={() => setForm((f) => ({ ...f, type: id }))}
                    style={{
                      flex: 1,
                      padding: '7px',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: form.type === id ? 'var(--ylw)' : 'var(--bg)',
                      color: form.type === id ? '#000' : 'var(--txt)',
                      border:
                        form.type === id ? '1.5px solid var(--ylw)' : '1.5px solid var(--bdr)',
                    }}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>
                {form.type === 'goal'
                  ? 'Has a target — vacation, car, down payment, etc.'
                  : 'Ongoing balance tracking — emergency fund, HYSA, etc.'}
              </div>
            </div>

            <div className="ef">
              <label>Name</label>
              <input
                className="ob-inp"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={form.type === 'goal' ? 'e.g. Vacation Fund' : 'e.g. Emergency Fund'}
              />
            </div>

            {form.type !== 'account' && (
              <div className="ef">
                <label>Target ($)</label>
                <input
                  type="number"
                  className="ob-inp"
                  value={form.target}
                  onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                  placeholder="0"
                  min="0"
                />
              </div>
            )}

            <div className="ef">
              <label>Emoji</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {GOAL_EMOJIS.map((em) => (
                  <button
                    key={em}
                    onClick={() => setForm((f) => ({ ...f, emoji: em }))}
                    style={{
                      fontSize: 18,
                      padding: '4px 6px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: form.emoji === em ? 'var(--ylw)' : 'var(--bg)',
                      border:
                        form.emoji === em ? '1.5px solid var(--ylw)' : '1.5px solid var(--bdr)',
                    }}
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>

            <div className="ef">
              <label>Color</label>
              <div className="ob-cols">
                {GOAL_COLORS.map((c) => (
                  <button
                    key={c}
                    className={'ob-col' + (form.col === c ? ' on' : '')}
                    style={{ background: c }}
                    onClick={() => setForm((f) => ({ ...f, col: c }))}
                  />
                ))}
              </div>
            </div>

            <div className="ea">
              <button className="bsave" onClick={addGoal}>
                Add
              </button>
              <button className="bcancel" onClick={() => setAdding(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className="dc"
            style={{
              borderStyle: 'dashed',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 100,
              color: 'var(--mut)',
              flexDirection: 'column',
              gap: 8,
            }}
            onClick={() => setAdding(true)}
          >
            <span style={{ fontSize: 24 }}>+</span>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Add goal or account</span>
          </div>
        )}
      </div>

      {goals.length > 0 && (
        <div
          style={{
            padding: '10px 14px',
            background: 'var(--surf)',
            border: '1px solid var(--bdr)',
            borderRadius: 10,
            fontSize: 12,
            color: 'var(--mut)',
          }}
        >
          💡 Log monthly deposits in <strong>Plan → Income &amp; Budget</strong> and pick which goal or account
          they go to.
        </div>
      )}
    </div>
  );
}
